import { Link } from "react-router-dom";
import Footer from "../Footer/Footer";
import { Helmet } from "react-helmet-async";
import { useI18n } from "../../../i18n/I18nContext";

export default function CustomerSupport() {
  const { t } = useI18n();

  const sections = [
    {
      id: "primeira-encomenda",
      title: "Primeira encomenda",
      content: (
        <>
          <p>
            {t(
              "Antes de fazer a primeira encomenda deve registar-se no nosso site. O registo é um processo simples e rápido."
            )}
          </p>
          <p>
            {t(
              "Deve adicionar os produtos pretendidos ao carrinho e finalizar a encomenda indicando a morada de entrega, faturação e o método de envio."
            )}
          </p>
          <p>
            {t(
              "Por questões de segurança, na primeira encomenda o método de envio à cobrança não está disponível."
            )}
          </p>
        </>
      ),
    },
    {
      id: "hora-expedicao",
      title: "Hora limite de expedição",
      content: (
        <>
          <p>
            {t(
              "As encomendas finalizadas até às 15:00 serão enviadas no próprio dia, salvo encomendas personalizadas."
            )}
          </p>
          <p>
            {t(
              "Depois dessa hora, não é possível garantir expedição no mesmo dia. A expedição é sempre feita por ordem de chegada."
            )}
          </p>
        </>
      ),
    },
    {
      id: "alteracoes",
      title: "Alterações à encomenda",
      content: (
        <>
          <p>
            {t(
              "Caso pretenda alterar materiais ou quantidades, deverá contactar-nos o mais rapidamente possível."
            )}
          </p>
          <p>
            {t(
              "Se a encomenda ainda estiver nas nossas instalações, procederemos à alteração da mesma."
            )}
          </p>
        </>
      ),
    },
    {
      id: "falta-material",
      title: "Falta de material",
      content: (
        <>
          <p>
            {t(
              "Se a encomenda ficar incompleta devido a rutura de stock, a Racksforall.eu enviará de imediato os artigos disponíveis."
            )}
          </p>
          <p>
            {t(
              "O restante material será enviado assim que chegue dos fornecedores, sem quaisquer custos ou portes adicionais."
            )}
          </p>
          <p>
            {t(
              "O cliente será sempre avisado no momento do primeiro envio."
            )}
          </p>
        </>
      ),
    },
    {
      id: "duvidas",
      title: "Esclarecimento de dúvidas",
      content: (
        <>
          <p>
            {t(
              "Caso tenha alguma dúvida relativa à sua encomenda, pode entrar em contacto com a nossa equipa através da página de contactos."
            )}
          </p>
        </>
      ),
    },
    {
      id: "precos",
      title: "Preços dos produtos",
      content: (
        <>
          <p>
            {t("Os preços dos produtos podem alterar sem aviso prévio.")}
          </p>
          <p>
            {t(
              "A Racksforall.eu não se responsabiliza pelo fornecimento de produtos com erros evidentes de preços no site."
            )}
          </p>
          <p>
            {t("Em caso de dúvida, deve contactar a Racksforall.eu.")}
          </p>
        </>
      ),
    },
    {
      id: "iva",
      title: "IVA",
      content: (
        <>
          <p>{t("Os preços apresentados não incluem IVA.")}</p>
          <p>
            {t("Aos valores apresentados acresce o IVA à taxa legal em vigor.")}
          </p>
        </>
      ),
    },
    {
      id: "stock",
      title: "Stock",
      content: (
        <>
          <p>
            {t(
              "Podem ocorrer erros de stock no inventário da Racksforall.eu."
            )}
          </p>
          <p>
            {t(
              "Deve confirmar a disponibilidade de qualquer produto antes de se deslocar à Racksforall.eu."
            )}
          </p>
        </>
      ),
    },
    {
      id: "transporte",
      title: "Transporte",
      content: (
        <>
          <p>
            {t(
              "Está excluído o envio de encomendas com mais de 20Kg para as Regiões Autónomas da Madeira e Açores."
            )}
          </p>
          <p>
            {t(
              "O transporte não é responsabilidade da Racksforall.eu. O cliente escolhe a transportadora, assume os seus custos e possíveis incidências."
            )}
          </p>
          <p>
            {t(
              "O destinatário deve verificar a mercadoria no momento da receção e colocar uma reserva clara e precisa no documento do transportador."
            )}
          </p>
          <p>
            {t(
              "Sem essa reserva, o transportador poderá declinar qualquer responsabilidade."
            )}
          </p>
        </>
      ),
    },
    {
      id: "reclamacoes",
      title: "Reclamações",
      content: (
        <>
          <p>
            {t(
              "Pedimos que verifique a mercadoria no momento da receção."
            )}
          </p>
          <p>{t("Não se admitirão reclamações após 72 horas.")}</p>
        </>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("Apoio ao Cliente | Racksforall")}</title>
        <meta
          name="description"
          content={t(
            "Contacta o apoio ao cliente da Racksforall para esclarecimentos sobre encomendas, produtos e serviços."
          )}
        />
        <link rel="canonical" href="https://racksforall.pt/support" />
        <meta property="og:title" content={t("Apoio ao Cliente | Racksforall")} />
        <meta
          property="og:description"
          content={t(
            "Contacta o apoio ao cliente da Racksforall para esclarecimentos sobre encomendas, produtos e serviços."
          )}
        />
        <meta property="og:url" content="https://racksforall.pt/support" />
      </Helmet>
      <main className="min-h-screen bg-stone-50 pt-24 text-slate-900 md:pt-8">
        <section className="border-b border-slate-200">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-medium uppercase tracking-[0.22em] text-slate-500">
                Racksforall.eu
              </p>

              <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
                {t("Apoio ao Cliente")}
              </h1>

              <p className="mt-6 text-lg leading-8 text-slate-600">
                {t(
                  "Informação prática sobre encomendas, expedição, alterações, transporte, preços e reclamações."
                )}
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-12 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:px-8">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="px-3 pb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                {t("Índice")}
              </p>

              <nav className="space-y-1">
                {sections.map((section, index) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="group flex items-center justify-between rounded-xl px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                  >
                    <span>{t(section.title)}</span>
                    <span className="text-xs text-slate-400 transition group-hover:text-slate-700">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </a>
                ))}
              </nav>

              <div className="mt-6 rounded-xl bg-slate-900 p-4 text-sm text-white">
                <p className="font-medium">{t("Precisas de ajuda direta?")}</p>

                <Link
                  to="/contact"
                  className="mt-3 inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-900 transition hover:bg-slate-200"
                >
                  {t("Ir para Contactos")}
                </Link>
              </div>
            </div>
          </aside>

          <div className="space-y-6">
            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="scroll-mt-24 border-t border-slate-200 py-6 first:border-t-0 first:pt-0"
              >
                <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                      {t("Secção")}
                    </p>
                    <h2 className="mt-2 text-xl font-semibold text-slate-900">
                      {t(section.title)}
                    </h2>
                  </div>

                  <div className="max-w-3xl space-y-4 text-[15px] leading-7 text-slate-600">
                    {section.content}
                  </div>
                </div>
              </section>
            ))}

            <section className="border-t border-slate-200 py-6">
              <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                    {t("Contacto")}
                  </p>
                  <h2 className="mt-2 text-xl font-semibold text-slate-900">
                    {t("Falar com a equipa")}
                  </h2>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6">
                  <p className="text-slate-600">
                    {t(
                      "Se precisares de apoio adicional sobre a tua encomenda, entra em contacto com a nossa equipa através da página de contactos."
                    )}
                  </p>

                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-700"
                  >
                    {t("Ir para Contactos")}
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
